// Money (BRIEF.md section 1): bank accounts and their statements, in plain words.
import { useState } from 'react';
import { useOrgData } from '../accountant/useOrgData.js';
import type { Api } from '../api.js';
import { day, dollars, fromCents } from '../format.js';
import { AddBankAccount } from './AddBankAccount.js';
import { BalanceCheck } from './BalanceCheck.js';
import { SortQueue } from './SortQueue.js';
import { StripeSection } from './Stripe.js';
import type { BankAccount, ImportRow } from './types.js';
import { UploadStatement } from './UploadStatement.js';

export function Money({ api, orgId }: { api: Api; orgId: string }) {
  const accounts = useOrgData<{ bankAccounts: BankAccount[] }>(api, orgId, '/bank-accounts');
  const [adding, setAdding] = useState(false);

  if (accounts.error) return <p className="error">{accounts.error}</p>;
  if (!accounts.data) return <p className="muted">Loading</p>;
  const list = accounts.data.bankAccounts;

  return (
    <section className="stack">
      <h1>Money</h1>
      {list.map((a) => (
        <BankAccountCard
          key={a.id}
          api={api}
          orgId={orgId}
          account={a}
          onChanged={accounts.reload}
        />
      ))}
      {list.length === 0 || adding ? (
        <AddBankAccount
          api={api}
          orgId={orgId}
          onAdded={() => {
            setAdding(false);
            void accounts.reload();
          }}
        />
      ) : (
        <button type="button" className="link" onClick={() => setAdding(true)}>
          Add another bank account
        </button>
      )}
      <StripeSection api={api} orgId={orgId} />
    </section>
  );
}

function BankAccountCard(props: {
  api: Api;
  orgId: string;
  account: BankAccount;
  onChanged: () => void;
}) {
  const { api, orgId, account } = props;
  const imports = useOrgData<{ imports: ImportRow[] }>(
    api,
    orgId,
    `/bank-accounts/${account.id}/imports`,
  );
  const [error, setError] = useState('');
  const [sorting, setSorting] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = () => {
    props.onChanged();
    void imports.reload();
    setVersion((v) => v + 1);
  };

  async function undo(id: string) {
    setError('');
    try {
      await api.send(orgId, 'POST', `/imports/${id}/undo`);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not undo the import.');
    }
  }

  const latest = imports.data?.imports.find((i) => i.status === 'imported');
  return (
    <div className="stack bank-card">
      <div className="title-row">
        <h2>
          {account.name}
          {account.account_number_last4 && (
            <span className="muted"> ending {account.account_number_last4}</span>
          )}
        </h2>
        {account.to_reconcile > 0 && <span className="pill">{account.to_reconcile} to sort</span>}
      </div>
      <p className="muted">
        {account.last_import
          ? `Imported up to ${day(account.last_import.period_end)}, closing balance ${dollars(fromCents(account.last_import.closing_minor))}.`
          : 'No statements imported yet.'}
      </p>
      <BalanceCheck api={api} orgId={orgId} bankAccountId={account.id} version={version} />
      {account.last_import && (
        <button
          type="button"
          className={sorting ? 'link' : undefined}
          onClick={() => setSorting((v) => !v)}
        >
          {sorting
            ? 'Hide'
            : account.to_reconcile > 0
              ? `Sort ${account.to_reconcile} transactions`
              : 'See sorted transactions'}
        </button>
      )}
      {sorting && (
        <SortQueue api={api} orgId={orgId} bankAccountId={account.id} onChanged={refresh} />
      )}
      <UploadStatement api={api} orgId={orgId} account={account} onImported={refresh} />
      {imports.data && imports.data.imports.length > 0 && (
        <table className="table">
          <caption>Statements</caption>
          <tbody>
            {imports.data.imports.map((i) => (
              <tr key={i.id} className={i.status === 'undone' ? 'muted' : undefined}>
                <td>
                  {day(i.period_start)} to {day(i.period_end)}
                </td>
                <td className="num">{i.line_count} transactions</td>
                <td className="num">{dollars(fromCents(i.closing_minor))}</td>
                <td>
                  {i.status === 'undone' ? (
                    'Undone'
                  ) : i.id === latest?.id ? (
                    <button type="button" className="link" onClick={() => undo(i.id)}>
                      Undo
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
